import { Coins, Timer, Trophy } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { apiMode } from '../../api/client';
import { useSite } from '../../app/site';
import { LogoMark } from '../../components/layout/Logo';
import { Card } from '../../components/ui/Card';
import { useLocale } from '../../lib/useLocale';
import { StepFade } from './StepFade';

const HIGHLIGHTS = [
  { icon: Trophy, titleKey: 'home.featureAchievementsTitle', bodyKey: 'home.featureAchievementsBody' },
  { icon: Timer, titleKey: 'home.featureSpeedrunTitle', bodyKey: 'home.featureSpeedrunBody' },
  { icon: Coins, titleKey: 'home.featurePointsTitle', bodyKey: 'home.featurePointsBody' },
] as const;

export function AuthLayout({
  title,
  subtitle,
  children,
  aside,
  fillViewport = false,
  fadeKey,
  fadeAppear = false,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  aside?: ReactNode;
  fillViewport?: boolean;
  fadeKey?: string;
  fadeAppear?: boolean;
}) {
  const { t } = useLocale();
  const { settings } = useSite();
  const siteName = settings?.siteName ?? t('common.appName');

  return (
    <div
      className={
        fillViewport
          ? 'grid min-h-screen lg:grid-cols-[1fr_minmax(26rem,32rem)]'
          : 'grid min-h-[calc(100vh-4rem)] lg:grid-cols-[1fr_minmax(26rem,32rem)]'
      }
    >
      {/* Brand panel — hidden on small screens where the form is the whole page. */}
      <aside className="border-line relative hidden overflow-hidden border-r lg:block">
        <div className="aurora absolute inset-0" aria-hidden="true" />
        <div className="grid-backdrop absolute inset-0 opacity-70" aria-hidden="true" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3">
            <LogoMark className="size-9" />
            <span className="font-display text-base font-semibold">{siteName}</span>
          </Link>

          <div className="max-w-md">
            <h2 className="text-brand text-3xl font-semibold">{t('home.title')}</h2>
            <p className="text-text-muted mt-4 text-sm">{t('home.subtitle')}</p>
            <ul className="mt-10 space-y-6">
              {HIGHLIGHTS.map((highlight) => (
                <li key={highlight.titleKey} className="flex gap-4">
                  <span className="bg-primary-soft text-primary mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md">
                    <highlight.icon className="size-4.5" />
                  </span>
                  <div>
                    <p className="text-sm font-medium">{t(highlight.titleKey)}</p>
                    <p className="text-text-subtle mt-1 text-xs">{t(highlight.bodyKey)}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-2xs text-text-faint">© {new Date().getFullYear()} Medaka Games</p>
        </div>
      </aside>

      <div className="flex items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <Link to="/" className="inline-flex items-center gap-2.5">
              <LogoMark className="size-8" />
              <span className="font-display text-sm font-semibold">{siteName}</span>
            </Link>
          </div>

          {fadeKey ? (
            <StepFade stepKey={fadeKey} appear={fadeAppear}>
              <h1 className="text-2xl font-semibold">{title}</h1>
              {subtitle ? <p className="text-text-muted mt-2 text-sm">{subtitle}</p> : null}
              <div className="mt-8">{children}</div>
              {aside}
            </StepFade>
          ) : (
            <>
              <h1 className="text-2xl font-semibold">{title}</h1>
              {subtitle ? <p className="text-text-muted mt-2 text-sm">{subtitle}</p> : null}
              <div className="mt-8">{children}</div>
              {aside}
            </>
          )}

          {apiMode === 'mock' ? (
            <Card tone="flat" className="mt-8 px-4 py-3">
              <p className="text-2xs text-text-faint">
                {t('auth.demoHint', { identifier: 'yu@medaka.games', password: 'medaka1234' })}
              </p>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
